import { AbstractControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import { dateMinMaxValidator } from '../../common/validators/date-min-max/date-min-max.validator';
import { IdentityBrokeringPolicyDto } from '../../common/interfaces/identity-brokering-policy-dto';
import { arrayToDate } from '../../common/utils/date-util';
import { SsoUserAttributeTypeDto } from '../../common/interfaces/sso-user-attribute-type-dto';
import { SsoUserAttributeDto } from '../../common/interfaces/sso-user-attribute-dto';
import { SsoUserDetailsDto } from '../../common/interfaces/sso-user-details-dto';

export function buildAttributesAsDtos(
    idpAttributes: SsoUserAttributeTypeDto[], form: FormGroup, ssoUserDetails?: SsoUserDetailsDto | null
): SsoUserAttributeDto[] {
    const attributes: SsoUserAttributeDto[] = [];
    for (const attr of idpAttributes) {
        const control = form.get(`idp_${attr.name}`);
        if (control) {
            attributes.push({ ssoGuid: ssoUserDetails?.id || '', value: control.value || '', type: attr });
        }
    }
    return attributes;
}

export function getExistingAttributeValue(ssoUserDetails: SsoUserDetailsDto | null | undefined, attributeName: string): string {
    const existingAttr = ssoUserDetails?.ssoUserAttributeDtos?.find(a => a.type.name === attributeName);
    return existingAttr?.value || '';
}

export const dateFormat = 'MM/dd/yyyy';
export const minBirthDate = '01/01/1880';

export function isFieldRequired(policy: IdentityBrokeringPolicyDto | null, field: 'dobRequired' | 'emailRequired'): boolean {
    return !policy?.active || (policy[field] ?? true);
}

export function isPasswordAllowed(policy: IdentityBrokeringPolicyDto | null): boolean {
    return !policy?.active || policy.storePasswords !== false;
}

export function createOptionalDateValidator(dobRequired: boolean, minDate: string, maxDate: string): ValidatorFn {
    const baseValidator = dateMinMaxValidator(minDate, maxDate);
    return (control: AbstractControl) => {
        if (!dobRequired) {
            const value = control.value;
            if (!value || value === '' || (value instanceof Date && isNaN(value.getTime()))) {
                return null;
            }
        }
        return baseValidator(control);
    };
}

export function getDateValidators(dobRequired: boolean): ValidatorFn[] {
    const maxDate = DateTime.now().toFormat(dateFormat);
    return dobRequired
        ? [Validators.required, dateMinMaxValidator(minBirthDate, maxDate)]
        : [createOptionalDateValidator(dobRequired, minBirthDate, maxDate)];
}

export function getEmailValidators(emailRequired: boolean): ValidatorFn[] {
    return emailRequired
        ? [Validators.required, Validators.maxLength(50), Validators.email]
        : [Validators.maxLength(50), Validators.email];
}

export function parseDateOfBirthValue(value: string | number | Date | number[]): Date {
    if (Array.isArray(value)) {
        return arrayToDate(value);
    } else if (typeof value === 'string') {
        return arrayToDate(value.split(',').map(Number));
    }
    return new Date(value);
}

export function togglePasswordVisibility(passwordInput: HTMLInputElement, passwordIcon: HTMLElement): void {
    if (passwordInput.type === 'password') {
        passwordInput.type = 'text';
        passwordIcon.classList.remove('fa-eye');
        passwordIcon.classList.add('fa-eye-slash');
    } else {
        passwordInput.type = 'password';
        passwordIcon.classList.remove('fa-eye-slash');
        passwordIcon.classList.add('fa-eye');
    }
}

/**
 * The email async validators (domain policy, uniqueness) bail out early unless the control
 * is dirty. When the email is still untouched by the user, force one dirty validation pass
 * so compliance status keeps getting computed for this existing user's already-saved email
 * (EMA-219631), then reset pristine so patchValue-driven loads and later revalidation triggers
 * (e.g. identityBrokeringPolicy changing) never register as an unsaved edit (EMA-236263).
 * If the user has actually edited the email, leave dirty tracking alone.
 */
export function revalidateEmailComplianceIfUnedited(form: FormGroup, isCreateMode: boolean): void {
    const emailControl = form.get('email');
    if (!emailControl) {
        return;
    }
    if (isCreateMode || !emailControl.pristine) {
        emailControl.updateValueAndValidity();
        return;
    }
    emailControl.markAsDirty();
    emailControl.updateValueAndValidity();
    emailControl.markAsPristine();
}

export function updateFieldValidators(
    form: FormGroup, dobRequired: boolean, emailRequired: boolean, isCreateMode: boolean, passwordAllowed: boolean
): void {
    const dobControl = form.get('dateOfBirth');
    if (dobControl) {
        dobControl.setValidators(getDateValidators(dobRequired));
        dobControl.updateValueAndValidity();
    }

    const emailControl = form.get('email');
    if (emailControl) {
        emailControl.setValidators(getEmailValidators(emailRequired));
        revalidateEmailComplianceIfUnedited(form, isCreateMode);
    }

    const passwordRequired = isCreateMode && passwordAllowed;
    for (const controlName of ['password', 'confirmPassword']) {
        const control = form.get(controlName);
        if (!control) {
            continue;
        }
        if (!passwordAllowed) {
            control.setValue('');
        }
        control.setValidators(passwordRequired ? [Validators.required] : [Validators.nullValidator]);
        control.updateValueAndValidity();
    }
}

/**
 * Values restored from the create-flow form state are applied with `patchValue`, which keeps the
 * controls pristine. The async validators (username/email uniqueness, email domain policy) skip
 * pristine controls, so reopening the drawer would leave an already-assigned email unvalidated and
 * the form saveable. Re-mark the restored controls as dirty/touched and revalidate them so the
 * remote checks run again on every reopen.
 */
export function revalidateRestoredControls(form: FormGroup, restoredData: Record<string, unknown>): void {
    for (const controlName of Object.keys(restoredData)) {
        const control = form.get(controlName);
        const value = restoredData[controlName];
        if (!control || value === null || value === undefined || value === '') {
            continue;
        }
        control.markAsDirty();
        control.markAsTouched();
        control.updateValueAndValidity();
    }
}
